import React from 'react';
import { graphql } from 'react-relay';

import styled from '@emotion/styled';

import BaseLayout from '@zego/layouts/BaseLayout';
import withData from '@zego/lib/withData';
import {
  BREAKPOINTS,
  disabledColor,
  infoColor,
  successColor,
  unit,
  white,
} from '@zego/theme';
import ThemeBlue from '@zego/themes/ThemeBlue';
import ThemeLightBlue from '@zego/themes/ThemeLightBlue';
import ThemeLightGreen from '@zego/themes/ThemeLightGreen';
import ThemeLightGrey from '@zego/themes/ThemeLightGrey';
import ThemeZegoPurple from '@zego/themes/ThemeZegoPurple';
import { getCountrySupportLink } from '@zego/utils/route';

import ArrowLink from '@zego/components/ArrowLink';
import H1 from '@zego/components/atoms/H1';
import H2 from '@zego/components/atoms/H2';
import RegularText from '@zego/components/atoms/RegularText';
import Spacer from '@zego/components/atoms/Spacer';
import View from '@zego/components/atoms/View';
import ContentContainer from '@zego/components/ContentContainer';
import ParagraphsBulletPointsSection from '@zego/components/ParagraphsBulletPointsSection';
import {
  LeftFeature,
  RightFeature,
} from '@zego/components/ProductFeatureBlock';
import ProductHero from '@zego/components/ProductHero';
import StaticImage from '@zego/components/StaticImage';

import harryImage from '@zego/static/images/founder-harry.jpg';
import stenImage from '@zego/static/images/founder-sten.jpg';
import stuImage from '@zego/static/images/founder-stu.jpg';
import heroImage from '@zego/static/images/hero-about.jpg';

import linkedIn from './linkedin.svg';
import partner1Logo from './partner1.png';
import partner2Logo from './partner2.png';

const GreenText = styled(RegularText)({
  fontSize: 18,
  color: successColor(5),
});

const FixedWidthColumn = styled(View)({
  display: 'flex',
  flexDirection: 'column',

  alignItems: 'center',
  [BREAKPOINTS.medium]: {
    width: 275,
  },
});

const OpacityLink = styled.a({
  opacity: 0.4,
  ':hover': {
    opacity: 1,
  },
});
const newWindowProps = {
  target: '_blank',
  rel: 'noopener noreferrer',
};

const PartnerLink = styled.a({
  width: 150,
  height: 150,
  display: 'flex',
  alignItems: 'center',
  padding: 8,
  ':hover': {
    backgroundColor: white,
    boxShadow: '0px 24px 30px -20px rgba(0,0,0,0.2)',
  },
});

const Section = styled(View)(
  {
    paddingTop: unit(10),
    paddingBottom: unit(10),
  },
  props => ({
    backgroundColor: props.alternate ? disabledColor(2) : white,
  }),
);

const ABOUT_US_PARAGRAPH_CONTENT = {
  sectionOne: {
    heading: 'Seguros para repartidores',
    paragraphs: [
      'Empezamos con una póliza por horas para repartidores que van en moto. Lo que queríamos conseguir era poner a su alcance un seguro totalmente flexible, que se adaptara a su manera de trabajar, con el que solo paguen por las horas trabajadas.',
      'En pocos meses se lanzó la aplicación de Zego, que permitía a sus usuarios gestionar su seguro a través de la aplicación.',
      'Desde entonces hemos lanzado más productos como seguros mensuales y anuales para repartidores y conductores privados. También diseñamos seguros de Responsabilidad Civil flexibles para autónomos, que les permite asegurar su actividad cuando lo necesiten',
    ],
    width: '100%',
    paragraphWidth: '100%',
    mobileTextAlign: 'left',
  },
  sectionTwo: {
    heading: 'Trabajo duro',
    paragraphs: [
      'Da igual si eres un conductor privado o un trabajador autónomo acabando un proyecto a tiempo. Sabemos que trabajas muy duro para cumplir con tus objetivos.',
      'Es por esto que nosotros también nos esforzamos con el fin de crear productos que permitan a nuestros clientes trabajar de forma segura cuando lo necesiten.',
    ],
    width: '100%',
    paragraphWidth: '100%',
    mobileTextAlign: 'left',
  },
  sectionThree: {
    heading: 'Crecimos',
    paragraphs: [
      'Pasamos de ser 12 personas a ser 50 en tan solo un año. Crecemos rápidamente con el objetivo de crear nuevos productos flexibles que se adapten a las necesidades de los trabajadores de la nueva economía.',
    ],
    width: '100%',
    paragraphWidth: '100%',
    mobileTextAlign: 'left',
  },
  sectionFour: {
    heading: 'Nos expandimos',
    paragraphs: [
      'No solo hemos crecido a nivel de producto, también lo hemos hecho a nivel de aliados con nuestra misma idea. Compartimos nuestro proyecto con todo aquel que tenga ganas de cambiar el mundo.',
      'También nos hemos expandido a nivel internacional ampliando las opciones de cambio para nuestros clientes. ¡Esto no ha hecho más que empezar!',
    ],
    width: '100%',
    paragraphWidth: '100%',
    mobileTextAlign: 'left',
  },
  sectionFive: {
    heading: 'Te escuchamos',
    paragraphs: [
      'Si algo hemos aprendido es que escuchar es fundamental ya que vuestra opinión nos hace mejores y nos ayuda adaptar nuestros productos a vuestras necesidades.',
      'No importa si eres un repartidor, un conductor privado o un trabajador autónomo, si crees que necesitas un producto que no tenemos dínoslo, ¡trabajaremos para tenerlo cuanto antes!',
    ],
    width: '100%',
    paragraphWidth: '100%',
    mobileTextAlign: 'left',
  },
};

const FOUNDER_CONTENT = {
  founderOne: {
    paragraphs: [
      'Nominado al premio para Jóvenes Empresarios Europeos por la revista Bloomberg Business Week en 2007, Sten se hizo famoso a los 17 años al fundar su propia empresa de blocs de notas inteligentes para estudiantes.',
      'La empresa llego a cotizar en cuatro países diferentes y obtuvo unos ingresos netos de un millón de euros.',
      'Después de esto, se trasladó a Londres para trabajar en la exitosa puesta en marcha de Quickstart Global, donde se encargó de la expansión internacional de la compañía. En 2011 se unió a la empresa de alquiler de propiedades Onefinestay donde ejerció como Jefe de operaciones.',
      'Antes de Zego, Sten trabajó en Deliveroo creando la estructura operativa de crecimiento global de la empresa.',
    ],
    width: '100%',
    paragraphWidth: '100%',
    mobileTextAlign: 'left',
  },
  founderTwo: {
    paragraphs: [
      'Con un total de nueve años de experiencia en el sector start up, Harry tuvo su primera experiencia en el sector tecnológico como Director de Marketing de Reevoo. Después trabajo en Wheel freedom, donde se centró en la formación de equipos y la contabilidad.',
      'Luego paso a formar parte del equipo de Onefinestay, donde se encargaba de la gestión de los propietarios y donde luego controló todas las propiedades de Londres.',
      'Antes de fundar Zego, Harry fue Director de Compras Globales de Deliveroo, donde se encargó de impulsar la compañía a nivel global.',
    ],
    width: '100%',
    paragraphWidth: '100%',
    mobileTextAlign: 'left',
  },
  founderThree: {
    paragraphs: [
      'De origen australiano, Stuart se mudó a Londres en el año 2010 para trabajar como Front-end Developer para el Ministry of Sound. Durante esta experiencia renovó la identidad visual e interactiva de la compañía.',
      'Después paso a Onefinestay, donde dirigió al equipo de programadores que se encargó de ampliar el alcance de la compañía a nivel global en 7 países del mundo.',
      'Al acabar su etapa en Onefinestay, pasó a formar parte de Mainframe como Lead Developer, donde se encargó de mejorar la experiencia de usuario en la app de la empresa.',
      'Antes de crear Zego, "Stu" fue el Director del equipo de desarrollo tecnológico de la exitosa start up Hubble, donde se encargó de reestructurar la plataforma y expandirla a nivel global.',
    ],
    width: '100%',
    paragraphWidth: '100%',
    mobileTextAlign: 'left',
  },
};

const OUR_PARTNERS_CONTENT = {
  paragraphs: [
    'Creemos que la mejor manera de hacer crecer este proyecto es trabajar con personas que compartan nuestra ambición por hacer que los seguros sean algo más sencillo.',
    'Es por esto que Balderton Capital y Local Globe, fondos de capital de riesgo, se han unido a nosotros. Ambos compartimos la idea de crear soluciones innovadoras que se adapten a las necesidades del cliente.',
  ],
  width: '100%',
  paragraphWidth: '100%',
  mobileTextAlign: 'left',
};
const AboutUsEsPage = ({ viewer }) => (
  <BaseLayout
    viewer={viewer}
    title="Zego makes insurance simpler, better and more flexible for delivery
  drivers, riders, contractors and businesses. Find flexible cover to
  suit your needs today."
  >
    <ProductHero image={heroImage} bgBlend="#12092bc9">
      <ProductHero.Header>
        <>
          Somos Zego.
          <br />
          Seguros flexibles.
        </>
      </ProductHero.Header>
      <ProductHero.SubHeader>
        Nacimos en 2016 y nos dimos cuenta de que a pesar de que la manera de
        trabajar de la gente estaba cambiando sus seguros seguían siendo los
        mismos.
      </ProductHero.SubHeader>

      <ProductHero.SubHeader>
        Fue entonces cuando pensamos, ¿Qué pasaría si encontramos la manera de
        hacer seguros que se adapten a todo tipo de demandas? Desde
        repartidores y conductores hasta autónomos o pequeñas empresas.
      </ProductHero.SubHeader>
    </ProductHero>

    <ThemeLightBlue>
      <LeftFeature>
        <ParagraphsBulletPointsSection
          {...ABOUT_US_PARAGRAPH_CONTENT.sectionOne}
        />
      </LeftFeature>
    </ThemeLightBlue>

    <ThemeZegoPurple>
      <RightFeature>
        <ParagraphsBulletPointsSection
          {...ABOUT_US_PARAGRAPH_CONTENT.sectionTwo}
        />
      </RightFeature>
    </ThemeZegoPurple>

    <ThemeLightGreen>
      <LeftFeature>
        <ParagraphsBulletPointsSection
          {...ABOUT_US_PARAGRAPH_CONTENT.sectionThree}
        />
      </LeftFeature>
    </ThemeLightGreen>

    <ThemeLightGrey>
      <RightFeature>
        <ParagraphsBulletPointsSection
          {...ABOUT_US_PARAGRAPH_CONTENT.sectionFour}
        />
      </RightFeature>
    </ThemeLightGrey>

    <ThemeBlue>
      <LeftFeature>
        <ParagraphsBulletPointsSection
          {...ABOUT_US_PARAGRAPH_CONTENT.sectionFive}
        />
        <View css={{ display: 'flex', alignItems: 'center' }}>
          <ArrowLink color={infoColor(5)} href={getCountrySupportLink('es')}>
            Seguimos en contacto
          </ArrowLink>
        </View>
      </LeftFeature>
    </ThemeBlue>

    <Spacer height={unit(5)} />

    <Section>
      <ContentContainer>
        <H1 css={{ textAlign: 'center', marginBottom: unit(4) }}>
          Fundadores
        </H1>
        <Spacer height={unit(4)} />

        <View
          css={{
            display: 'flex',
            flexDirection: 'column',
            [BREAKPOINTS.medium]: {
              flexDirection: 'row',
            },
          }}
        >
          <FixedWidthColumn>
            <View
              css={{
                width: 275,
              }}
            >
              <StaticImage
                css={{ width: '100%' }}
                alt="Sten Saar"
                src={stenImage}
                ratio={2 / 3}
              />
              <Spacer height={unit(3)} />
              <H2>Sten Saar</H2>
              <Spacer height={unit(2)} />
              <GreenText>CEO</GreenText>
            </View>
            <Spacer height={unit(5)} />
            <ParagraphsBulletPointsSection {...FOUNDER_CONTENT.founderOne} />
            <Spacer height={unit(4)} />
            <OpacityLink
              href="https://www.linkedin.com/in/stensaar/"
              {...newWindowProps}
              css={{
                alignSelf: 'flex-start',
              }}
            >
              <img alt="LinkedIn icon" src={linkedIn} css={{ width: 20 }} />
            </OpacityLink>
            <Spacer height={unit(14)} />
          </FixedWidthColumn>

          <Spacer css={{ flexGrow: 1, minWidth: unit(2) }} />

          <FixedWidthColumn>
            <View css={{ width: 275 }}>
              <StaticImage
                css={{ width: '100%' }}
                alt="Harry Franks"
                src={harryImage}
                ratio={2 / 3}
              />
              <Spacer height={unit(3)} />
              <H2>Harry Franks</H2>
              <Spacer height={unit(2)} />
              <GreenText>CCO</GreenText>
            </View>
            <Spacer height={unit(5)} />
            <ParagraphsBulletPointsSection {...FOUNDER_CONTENT.founderTwo} />

            <Spacer height={unit(4)} />
            <OpacityLink
              href="https://www.linkedin.com/in/harryfranks/"
              {...newWindowProps}
              css={{
                alignSelf: 'flex-start',
              }}
            >
              <img alt="LinkedIn icon" src={linkedIn} css={{ width: 20 }} />
            </OpacityLink>
            <Spacer height={unit(14)} />
          </FixedWidthColumn>

          <Spacer css={{ flexGrow: 1, minWidth: unit(2) }} />

          <FixedWidthColumn>
            <View
              css={{
                width: 275,
              }}
            >
              <StaticImage
                css={{ width: '100%' }}
                alt="Stuart Kelly"
                src={stuImage}
                ratio={2 / 3}
              />
              <Spacer height={unit(3)} />
              <H2>Stuart Kelly</H2>
              <Spacer height={unit(2)} />
              <GreenText>CTO</GreenText>
            </View>
            <Spacer height={unit(5)} />
            <ParagraphsBulletPointsSection {...FOUNDER_CONTENT.founderThree} />
            <Spacer height={unit(4)} />
            <OpacityLink
              href="https://www.linkedin.com/in/stuart-kelly-0762a420/"
              {...newWindowProps}
              css={{
                alignSelf: 'flex-start',
              }}
            >
              <img alt="LinkedIn icon" src={linkedIn} css={{ width: 20 }} />
            </OpacityLink>
            <Spacer height={unit(14)} />
          </FixedWidthColumn>
        </View>
      </ContentContainer>
    </Section>

    <Section alternate={true}>
      <ContentContainer>
        <View
          css={{
            display: 'flex',
            flexDirection: 'column',
            [BREAKPOINTS.medium]: {
              flexDirection: 'row',
            },
          }}
        >
          <View css={{ flex: 1 }}>
            <H1>Nuestros socios</H1>
          </View>
          <Spacer width={unit(4)} height={unit(3)} />
          <View css={{ flex: 1 }}>
            <ParagraphsBulletPointsSection {...OUR_PARTNERS_CONTENT} />
          </View>
        </View>

        <Spacer height={unit(10)} />
        <View
          css={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
          }}
        >
          <PartnerLink href="https://www.balderton.com/" {...newWindowProps}>
            <img alt="Balderton logo" src={partner1Logo} width={'100%'} />
          </PartnerLink>
          <Spacer width={unit(10)} />
          <PartnerLink href="https://localglobe.vc/" {...newWindowProps}>
            <img alt="LocalGlobe logo" src={partner2Logo} width={'100%'} />
          </PartnerLink>
        </View>
      </ContentContainer>
    </Section>
  </BaseLayout>
);

export default withData(AboutUsEsPage, {
  query: graphql`
    query AboutUsEsPageQuery($country: String!) {
      viewer {
        ...BaseLayout_viewer @arguments(country: $country)
      }
    }
  `,
  getVariables: ({ query }) => {
    return {
      country: query.country,
    };
  },
});
